import {
    AppsListDetailFilled,
    AppsListDetailRegular,
    bundleIcon,
    ChartPersonFilled,
    ChartPersonRegular,
    ChevronDoubleDown16Regular,
    ChevronDoubleUp16Regular,
    ClipboardCheckmarkFilled,
    ClipboardCheckmarkRegular,
    Dismiss12Regular,
    FlagFilled,
    FlagRegular,
    FlowchartFilled,
    FlowchartRegular,
    HandshakeFilled,
    HandshakeRegular,
    MoneyFilled,
    MoneyRegular,
    PersonQuestionMarkFilled,
    PersonQuestionMarkRegular,
    PuzzleCubePieceRegular,
    StackStarFilled,
    StackStarRegular,
    TableSimpleExcludeFilled,
    TableSimpleExcludeRegular,
    TargetArrowFilled,
    TargetArrowRegular,
} from '@fluentui/react-icons';
import { EngagementTemplate, FieldEntry, useInitialValues } from '../../../data/types';
import { Column, Row } from '../../common';
import { SettingsPageTitle } from '../Settings/SettingsPageTitle';
import {
    Button,
    Checkbox,
    Divider,
    Field,
    InfoLabel,
    Input,
    makeStyles,
    mergeClasses,
    Tab,
    TabList,
    Text,
    Textarea,
    tokens,
} from '@fluentui/react-components';
import { useFormik } from 'formik';
import { MouseEvent, useEffect, useState } from 'react';
import {
    AddEngagementFieldDropdown,
    EngagementDocDefaultFields,
} from '../Cases/CaseEngagementDoc/AddEngagementFieldDropdown';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { FormSection } from '../../common/FormSection';
import { useValidation } from '../../../data/types/validation';
import { appTokens } from '../../common/Styles/appTokens';

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
    tabList: {
        width: '100%',
    },
    tab: {
        width: '100%',
        height: '2.5rem',
        ':hover': {
            backgroundColor: `${tokens.colorNeutralBackground2Hover} !important`,
        },
    },
});

export type AddEditEngagementTemplatePageProps = {
    templateToEdit?: EngagementTemplate;
    onSubmit: (template: EngagementTemplate) => Promise<void>;
    onCancel: () => void;
};
export const AddEditEngagementTemplatePage = (props: AddEditEngagementTemplatePageProps) => {
    const { templateToEdit, onSubmit, onCancel } = props;

    //Hooks
    const initValues = useInitialValues();
    const { validateEngagementTemplate, validateFieldEntry } = useValidation();

    //State
    const [selectedTab, setSelectedTab] = useState<string>();

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const fieldClasses = mergeClasses(styles.field, commonStyles.text.field);
    const tabClasses = mergeClasses(styles.tab, commonStyles.button.root);

    //Form
    const initTemplate = (): EngagementTemplate => {
        if (!!templateToEdit) {
            return {
                ...initValues.engagementTemplate(),
                ...templateToEdit,
            };
        }
        return initValues.engagementTemplate();
    };
    const templateForm = useFormik<EngagementTemplate>({
        initialValues: initTemplate() as EngagementTemplate,
        onSubmit: async (values, { resetForm }) => {
            const engTemplate = values as EngagementTemplate;

            if (!!engTemplate.templateName) {
                !!onSubmit && (await onSubmit(engTemplate));

                resetForm({ values });
            }
        },
        validationSchema: validateEngagementTemplate.schema,
    });

    //Logic

    //UI Events
    const onClickSubmit = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        templateForm.submitForm();
    };
    const onClickCancel = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        !!onCancel && onCancel();
    };
    const resetForm = () => {
        templateForm.resetForm({ values: initTemplate() });
    };
    const clickInput = (tab: string) => {
        setSelectedTab(tab);

        const allFocusable = document.querySelectorAll<HTMLElement>('[data-name]');
        const target = Array.from(allFocusable).find(
            (el) => el.dataset.name === `fields.${tab}` || el.dataset.name === tab,
        );
        target?.focus();
    };
    const addField = (fieldName?: string) => {
        if (!!fieldName && !templateForm.values.fields?.some((x) => x.fieldName == fieldName)) {
            const fieldsArray = templateForm.values.fields || [];

            templateForm.setFieldValue('fields', [...fieldsArray, { fieldName, fieldValue: '' }]);
        }
    };
    const onClickRemoveField = (fieldName: string) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!!fieldName) {
            const entries = templateForm.values.fields?.filter((x) => x.fieldName != fieldName);

            templateForm.setFieldValue('fields', entries);
        }
    };
    const onClickMoveFieldUp = (fieldName: string) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        const fieldsArray = templateForm.values.fields || [];
        const index = fieldsArray.findIndex((x) => x.fieldName == fieldName);

        if (index > 0) {
            const newArray = [
                ...fieldsArray.slice(0, index - 1),
                fieldsArray[index],
                fieldsArray[index - 1],
                ...fieldsArray.slice(index + 1),
            ];

            templateForm.setFieldValue('fields', newArray);
        }
    };
    const onClickMoveFieldDown = (fieldName: string) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        const fieldsArray = templateForm.values.fields || [];
        const index = fieldsArray.findIndex((x) => x.fieldName == fieldName);

        if (index < fieldsArray.length - 1) {
            const newArray = [
                ...fieldsArray.slice(0, index),
                fieldsArray[index + 1],
                fieldsArray[index],
                ...fieldsArray.slice(index + 2),
            ];

            templateForm.setFieldValue('fields', newArray);
        }
    };
    const onClickFieldTab = (fieldName: string) => (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        clickInput(fieldName);
    };

    const onFocusFieldTextArea = (fieldName: string) => () => setSelectedTab(fieldName);

    //Icons
    const ObjectivesIcons = bundleIcon(TargetArrowFilled, TargetArrowRegular);
    const ClientRespIcons = bundleIcon(ChartPersonFilled, ChartPersonRegular);
    const ServicesIcons = bundleIcon(StackStarFilled, StackStarRegular);
    const DeliverablesIcons = bundleIcon(ClipboardCheckmarkFilled, ClipboardCheckmarkRegular);
    const MilestonesIcons = bundleIcon(FlagFilled, FlagRegular);
    const AssumptionsIcons = bundleIcon(PersonQuestionMarkFilled, PersonQuestionMarkRegular);
    const ExclusionsIcons = bundleIcon(TableSimpleExcludeFilled, TableSimpleExcludeRegular);
    const DependenciesIcons = bundleIcon(FlowchartFilled, FlowchartRegular);
    const AcceptanceIcons = bundleIcon(HandshakeFilled, HandshakeRegular);
    const PaymentIcons = bundleIcon(MoneyFilled, MoneyRegular);
    const CustomIcons = bundleIcon(AppsListDetailFilled, AppsListDetailRegular);

    const getIconForField = (fieldName: string) => {
        if (fieldName == EngagementDocDefaultFields.objectives) return <ObjectivesIcons />;
        else if (fieldName == EngagementDocDefaultFields.clientResponsibilities)
            return <ClientRespIcons />;
        else if (fieldName == EngagementDocDefaultFields.servicesProvided) return <ServicesIcons />;
        else if (fieldName == EngagementDocDefaultFields.deliverables) return <DeliverablesIcons />;
        else if (fieldName == EngagementDocDefaultFields.milestones) return <MilestonesIcons />;
        else if (fieldName == EngagementDocDefaultFields.assumptions) return <AssumptionsIcons />;
        else if (fieldName == EngagementDocDefaultFields.exclusions) return <ExclusionsIcons />;
        else if (fieldName == EngagementDocDefaultFields.dependencies) return <DependenciesIcons />;
        else if (fieldName == EngagementDocDefaultFields.acceptanceCriteria)
            return <AcceptanceIcons />;
        else if (fieldName == EngagementDocDefaultFields.billingTerms) return <PaymentIcons />;
        else if (fieldName == EngagementDocDefaultFields.paymentTerms) return <PaymentIcons />;
        else return <CustomIcons />;
    };

    //Effects
    useEffect(() => {
        if (!!templateToEdit) {
            resetForm();
        }
    }, [templateToEdit]);

    return (
        <>
            <Column padding='px-3' flex='1' maxHeight={appTokens.pageHeight('6.25rem')}>
                <Column height='auto'>
                    <SettingsPageTitle
                        title='Add/Edit Engagement Template'
                        icon={<PuzzleCubePieceRegular style={{ fontSize: '1.625em' }} />}
                        action={
                            <Row gap='medium' padding='pr-3'>
                                <Button
                                    appearance='primary'
                                    onClick={onClickSubmit}
                                    disabled={!templateForm.dirty || !templateForm.isValid}
                                    data-testid='edit-engagement-template-save-button'
                                >
                                    Save
                                </Button>
                                <Button
                                    appearance='secondary'
                                    onClick={onClickCancel}
                                    data-testid='edit-engagement-template-cancel-button'
                                >
                                    Cancel
                                </Button>
                            </Row>
                        }
                    />
                </Column>
                <Column flex='1' gap='medium'>
                    <Column height='auto'>
                        <Row>
                            <Column>
                                <Field
                                    label='Template Name'
                                    className={fieldClasses}
                                    required
                                    validationMessage={templateForm.errors.templateName}
                                >
                                    <Input
                                        name='templateName'
                                        value={templateForm.values.templateName}
                                        onChange={templateForm.handleChange}
                                        data-testid='edit-engagement-template-name-input'
                                    />
                                </Field>
                                <Field
                                    label='Document Title'
                                    className={fieldClasses}
                                    required
                                    validationMessage={templateForm.errors.title}
                                >
                                    <Input
                                        name='title'
                                        value={templateForm.values.title}
                                        onChange={templateForm.handleChange}
                                        placeholder='Scope of Work, Retention Agreement, Engagement Letter,  etc.'
                                        data-testid='edit-engagement-template-title-input'
                                    />
                                </Field>
                            </Column>
                            <Column>
                                <Field
                                    label='Template Description'
                                    className={fieldClasses}
                                    required
                                    validationMessage={templateForm.errors.description}
                                    hint={`${templateForm.values.description?.length || 0} / ${validateEngagementTemplate.description.maxLength}`}
                                >
                                    <Textarea
                                        name='description'
                                        value={templateForm.values.description}
                                        onChange={templateForm.handleChange}
                                        maxLength={validateEngagementTemplate.description.maxLength}
                                        data-testid='edit-engagement-template-description-input'
                                    />
                                </Field>
                                <Checkbox
                                    name='addSignatures'
                                    label={
                                        <InfoLabel info='This will add the signatures section to the engagement document.'>
                                            Add Signatures
                                        </InfoLabel>
                                    }
                                    checked={templateForm.values.addSignatures}
                                    onChange={templateForm.handleChange}
                                    data-testid='edit-engagement-template-add-signatures-checkbox'
                                />
                            </Column>
                        </Row>
                    </Column>
                    <Text size={400} weight='semibold' italic>
                        Fields
                    </Text>

                    <Column
                        flex='1'
                        maxHeight={appTokens.pageHeight('24rem')}
                        subtleBorder
                        borderRadius='10px'
                        padding='px-3 py-3'
                    >
                        <Row flex='1'>
                            <Column
                                gap='small'
                                width='auto'
                                maxHeight={appTokens.pageHeight('26rem')}
                                scroll
                            >
                                <TabList
                                    className={styles.tabList}
                                    selectedValue={selectedTab}
                                    vertical
                                    data-testid='edit-engagement-template-tab-list'
                                >
                                    {templateForm.values.fields?.map((field: FieldEntry) => (
                                        <Tab
                                            key={field.fieldName}
                                            className={tabClasses}
                                            value={field.fieldName}
                                            icon={getIconForField(field.fieldName as string)}
                                            onClick={onClickFieldTab(field.fieldName as string)}
                                            data-testid={`edit-engagement-template-field-tab-${field.fieldName}`}
                                        >
                                            {field.fieldName}
                                        </Tab>
                                    ))}
                                </TabList>
                                {!!templateForm.values.fields?.length && (
                                    <Divider
                                        style={{ maxHeight: '.25rem', paddingBlock: '.5rem' }}
                                    />
                                )}
                                <Column align='left'>
                                    <AddEngagementFieldDropdown
                                        addField={addField}
                                        fieldNames={templateForm.values.fields?.map(
                                            (field) => field.fieldName || '',
                                        )}
                                    />
                                </Column>
                            </Column>

                            <Column
                                flex='1'
                                padding='pr-3'
                                maxHeight={appTokens.pageHeight('26rem')}
                                scroll
                            >
                                {templateForm.values.fields?.map((field, index, arr) => (
                                    <FormSection
                                        key={field.fieldName}
                                        title={field.fieldName}
                                        minHeight='fit-content'
                                        action={
                                            <Row noGap>
                                                <Button
                                                    className={commonStyles.button.root}
                                                    appearance='subtle'
                                                    icon={<ChevronDoubleUp16Regular />}
                                                    onClick={onClickMoveFieldUp(
                                                        field?.fieldName || '',
                                                    )}
                                                    disabled={index == 0}
                                                    data-testid={`edit-engagement-template-move-up-button-${field.fieldName}`}
                                                />
                                                <Button
                                                    className={commonStyles.button.root}
                                                    appearance='subtle'
                                                    icon={<ChevronDoubleDown16Regular />}
                                                    onClick={onClickMoveFieldDown(
                                                        field?.fieldName || '',
                                                    )}
                                                    disabled={index == arr.length - 1}
                                                    data-testid={`edit-engagement-template-move-down-button-${field.fieldName}`}
                                                />
                                                <Button
                                                    className={commonStyles.button.root}
                                                    appearance='subtle'
                                                    icon={<Dismiss12Regular color='red' />}
                                                    onClick={onClickRemoveField(
                                                        field.fieldName || '',
                                                    )}
                                                    data-testid={`edit-engagement-template-remove-button-${field.fieldName}`}
                                                />
                                            </Row>
                                        }
                                    >
                                        {field.fieldName ==
                                        EngagementDocDefaultFields.milestones ? (
                                            <Column
                                                subtleBorder
                                                borderRadius='5px'
                                                padding='px-2 py-2'
                                            >
                                                <Text weight='semibold'>
                                                    This will show the Milestones as defined in the
                                                    Case.
                                                </Text>
                                            </Column>
                                        ) : (
                                            <Field
                                                size='medium'
                                                hint={`${field.fieldValue?.length || 0} / ${validateFieldEntry.fieldValue.maxLength}`}
                                            >
                                                <Textarea
                                                    name={`fields.[${index}].fieldValue`}
                                                    data-name={field.fieldName}
                                                    tabIndex={index}
                                                    rows={5}
                                                    value={field.fieldValue}
                                                    onChange={templateForm?.handleChange}
                                                    onFocus={onFocusFieldTextArea(
                                                        field.fieldName as string,
                                                    )}
                                                    placeholder={`Enter ${field.fieldName}`}
                                                    data-testid={`edit-engagement-template-textarea-${field.fieldName}`}
                                                />
                                            </Field>
                                        )}
                                    </FormSection>
                                ))}
                            </Column>
                        </Row>
                    </Column>
                </Column>
            </Column>
        </>
    );
};
